import React, { useEffect, useMemo } from 'react'
import LoadingPageV2 from '../LoadingPageV2'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import CustomHeader from '../../components/CustomHeader'
import PAGE_TITLES from '../../consts/titles'
import ReportsFeature from '../../features/ReportsFeatureV2'
import ROUTES from '../../consts/paths'
import { ROLES } from '../../consts/roles'
import SkipNavigation from '../../components/SkipNavigation'
import { notification } from '../../components/Notifications'
import { notifyError } from '../../utils/notifyError'
import { Box, Container, Stack } from '@mui/material'
import SpaceDashboardOutlinedIcon from '@mui/icons-material/SpaceDashboardOutlined'
import BarChartIcon from '@mui/icons-material/BarChart'
import GenericButton from '../../components/GenericButton'
import { ButtonSize, ButtonType } from '../../components/GenericButton/styles'
import CustomTooltip from '../../components/CustomTooltip'
import { grey } from '../../colors'
import ReportsControls from '../../features/ReportsFeatureV2/components/ReportsControls'
import { buildExportConfigParams, INSTRUCTOR_BULK_EXPORT_MODAL_PROPS, type ExportConfig } from '../../features/ReportsFeatureV2/components/BulkExportModal'
import PageHeader from '../../components/PageHeader'
import GenericModal from '../../components/GenericModal'
import { prepareBreadCrumbs } from '../../utils/BreadcrumbDataPreparation'
import ReportAttributes from '../../features/ReportsFeatureV2/components/ReportAttributes'
import ReportAttributeLine from '../../features/ReportsFeatureV2/components/ReportAttributeLine'
import { DivisionManager, UserManager } from '../../APIManager'
import { type ReportAccessLevel } from '../../APIManager/UserManager'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { saveAs } from 'file-saver'
import { useTaskStatus } from '../../hooks/useTaskStatus'
import { ExportBackgroundTaskRunningModal } from '../../components/ExportBackgroundTaskRunningModal'
import { buildCommentsExportPayload, getFinalReportPayload, getReportsRequestType, getResponseFilterQuestionOptions, handleReportNotifications, isBaseReportDisplaySettings, isInsufficientResponsesError, isNoReportAccessError, normalizeDisplaySettings, normalizeNonMultiReport, normalizeNonMultiReportCalculations, normalizeNonMultiSurveyItem, resolveExportErrorMessage, scopedSliceKey, shouldConfirmResponseFilterSwitch } from '../../features/ReportsFeatureV2/components/helper'
import { AppliedAttributeScopeContext, useFetchCurrentUserAttributeScope } from '../../features/ReportsFeatureV2/hooks/useCurrentUserAttributeScope'
import { buildReportTitlesFromSettings } from '../../features/ReportsFeatureV2/components/reportTitles'
import { ReportsActionType, ReportsProvider, useReportsContext } from '../../features/ReportsFeatureV2/ReportsContext'
import { allCalculationKeys } from '../../features/ReportsFeatureV2/components/constants'

import { ProjectAndTermHeader } from '../../features/ReportsFeatureV2/components/ProjectAndTermHeader'
import { useUserRole } from '../../hooks/useUserRole'
import ReportHeaderChips from '../../features/ReportsFeatureV2/components/ReportHeaderChips'
import {
  buildLoadingSectionsFromBaseReport,
  resolveReportInsightsLoadingSections,
  type ReportInsightsLoadingSection
} from '../../features/ReportsFeatureV2/components/reportInsightsLoading'
import { type SurveyResponseMenuSelection } from '../../features/ReportsFeatureV2/components/Charts/BarChart'
import { type DisplaySettings } from '../../features/ReportsFeatureV2/types'
import { type ICalculation, type KeyStatSurveyItem, type ReportsV2, type SurveyResponseFilter } from '../../models/Report'
import { type DivisionCategory, type SurveyCompletionRate } from '../../models/Division'
import { SlotStatus } from '../../models/DistributionPlan'

const RESPONSE_FILTER_MINIMUM_COUNT = 5
const RESPONSE_FILTER_WARNING_MESSAGE = 'You need 5 or more responses to show.'

interface ResponseFilterAnswerSelection {
  answer: string
  count: number
}

interface InstructorModeConfig {
  instructorId?: string
  selfUserId?: string
  instructorName: string
  // Deliberately no card list here. The report view's only use for it was a client-side
  // completion sum, which is gone (BC-6) — and with the dashboard now paginated, any figure
  // derived from it would describe one page rather than the instructor. Keeping the report
  // page independent of the dashboard's paged data is what prevents that being reintroduced.
  objectId?: string
  extraControls?: React.ReactNode
}

export interface ReportsPageProps {
  instructorMode?: InstructorModeConfig
}

const ReportsPage: React.FC<ReportsPageProps> = ({ instructorMode }): JSX.Element => {
  const { selectedTrend, dispatch, displaySettings } = useReportsContext()
  const displaySettingsRef = React.useRef(displaySettings)
  displaySettingsRef.current = displaySettings
  const { isGlobalOrGenericRole, isGlobalRole, hasRole, activeRole } = useUserRole()
  const { taskId, setTaskId, showBackgroundModal, setShowBackgroundModal, checkStatusBeforeExport } = useTaskStatus('exportTask_reports')

  const { id: divisionId } = useParams()
  const [searchParams] = useSearchParams()
  const seriesId = searchParams.get('projectId')
  const termId = searchParams.get('termId')
  const fromInstructorId = searchParams.get('fromInstructorId')
  const rawSectionSlotStatus = searchParams.get('slotStatus')
  const sectionSlotStatus = Object.values(SlotStatus).includes(rawSectionSlotStatus as SlotStatus)
    ? rawSectionSlotStatus as SlotStatus
    : null
  // No sandbox parameter here, deliberately. Every read on this page is already scoped to one
  // Survey Project — `termId` and `distributionSeriesId` resolve to a single slot, and the
  // backend refuses the call without both — so there is no set for the preference to narrow. The
  // audience rule decides whether the caller could have chosen a sandboxed project at all, which
  // it does whether or not this page says anything. The parameter belongs to the picker above,
  // which is the one read that returns many projects (Backend `637ac2886`, 2026-09-21).
  const params = { termId, distributionSeriesId: seriesId, pageSize: 200 }
  const [selectedTab, setSelectedTab] = React.useState<string>('')
  const [isBulkExportModalOpen, setIsBulkExportModalOpen] = React.useState(false)
  const [exportModalPreset, setExportModalPreset] = React.useState<'Aggregate Report' | 'Comments Export'>('Aggregate Report')
  const [baseReportLoadingSections, setBaseReportLoadingSections] = React.useState<ReportInsightsLoadingSection[] | null>(null)
  const [responseFilterOverrideSurveyItem, setResponseFilterOverrideSurveyItem] = React.useState<KeyStatSurveyItem | null>(null)
  const [responseFilterHighlightedAnswers, setResponseFilterHighlightedAnswers] = React.useState<string[]>([])
  const [responseFilterExistingCalculations, setResponseFilterExistingCalculations] = React.useState<ICalculation[] | null>(null)
  const [originalBaseReport, setOriginalBaseReport] = React.useState<ReportsV2 | null>(null)
  const [responseFilterAnswerSelections, setResponseFilterAnswerSelections] = React.useState<ResponseFilterAnswerSelection[]>([])
  const [responseFilterWarningMessage, setResponseFilterWarningMessage] = React.useState<string | null>(null)
  const [pendingResponseFilterSelection, setPendingResponseFilterSelection] = React.useState<SurveyResponseMenuSelection | null>(null)
  const [pendingDisplaySettingsSubmission, setPendingDisplaySettingsSubmission] = React.useState<DisplaySettings | null>(null)
  // Read off the base report fetch's response header (CE-125). Not re-derived by the
  // history/responders request types, which don't carry this header — see spec.
  const [reportAccessLevel, setReportAccessLevel] = React.useState<ReportAccessLevel | null>(null)
  // Bridges the value from mutationFn (where it's read off the response) to onSuccess (where
  // state setters conventionally belong) without changing the mutation's ReportsV2 return type.
  const pendingReportAccessRef = React.useRef<ReportAccessLevel | null>(null)
  const pendingResponseFilterScrollRestoreRef = React.useRef<number | null>(null)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const emptyReports = React.useMemo<ReportsV2>(() => ({
    calculations: [],
    surveySections: [],
    titles: [],
    surveyScores: []
  }), [])

  // Use cached topBarDivisions if available, otherwise fetch via API
  const divisionAndSectionsTopbar = queryClient.getQueryData<any[]>(['topBarDivisions'])
  const {
    data: divisionAndSectionsApi,
    isLoading: isDivisionLoading,
    isError: isTopBarDivisionsError,
    error: topBarDivisionsError
  } = useQuery({
    queryKey: ['topBarDivisions'],
    queryFn: async () => {
      const response = await UserManager.getTopBarDivisions()
      return response as any
    },
    enabled: !divisionAndSectionsTopbar && !instructorMode && isGlobalOrGenericRole
  })

  // Use cached data if present, otherwise use API data
  const divisionAndSections = divisionAndSectionsTopbar || divisionAndSectionsApi

  useEffect(() => {
    if (instructorMode) return
    if (
      !divisionId &&
      Array.isArray(divisionAndSections) &&
      divisionAndSections.length > 0 &&
      !isDivisionLoading
    ) {
      const firstDivisionId = divisionAndSections[0]?.divisionId
      if (firstDivisionId) {
        navigate({
          pathname: `${ROUTES.REPORTS}/${firstDivisionId}`,
          search: searchParams.toString()
        })
      }
    }
  }, [divisionAndSections, isDivisionLoading, divisionId, navigate, searchParams])

  // On division ID change, reset the 'comparisons' and 'slices' fields in displaySettings in context.
  useEffect(() => {
    if (divisionId) {
      dispatch({
        type: ReportsActionType.SET_DISPLAY_SETTINGS,
        payload: {
          ...displaySettingsRef.current,
          comparisons: [{ compareBy: null, comparison: null, comparisonOption: null }],
          slices: [],
          responseFilter: null
        }
      })
    }
  }, [divisionId, selectedTrend])

  useEffect(() => {
    dispatch({
      type: ReportsActionType.SET_DISPLAY_SETTINGS,
      payload: {
        ...displaySettingsRef.current,
        responseFilter: null
      }
    })
    dispatch({
      type: ReportsActionType.SET_DISPLAY_SETTINGS_CHANGED,
      payload: false
    })
    setResponseFilterOverrideSurveyItem(null)
    setResponseFilterHighlightedAnswers([])
    setResponseFilterExistingCalculations(null)
    setResponseFilterAnswerSelections([])
    setResponseFilterWarningMessage(null)
  }, [seriesId, termId])

  // In instructor mode, divisionId is the instructor's user UUID — not a valid division UUID.
  // Only use a scope for breadcrumbs when a real objectId (division scope) is present.
  const breadcrumbScopeId = instructorMode ? (instructorMode.objectId ?? null) : divisionId
  const { data: breadCrumbData, isLoading: breadcrumbLoading, isError: isBreadcrumbError, error: breadcrumbError } = useQuery({
    queryKey: ['reportsBreadcrumbs', breadcrumbScopeId],
    queryFn: async () => await DivisionManager.getBreadCrumbList(breadcrumbScopeId ?? ''),
    enabled: !!breadcrumbScopeId
  })
  const breadcrumbSearchParams = React.useMemo(() => {
    if (!instructorMode) return searchParams
    const sp = new URLSearchParams(searchParams)
    sp.delete('objectId')
    sp.delete('view')
    return sp
  }, [instructorMode, searchParams])
  const baseBreadCrumbList = prepareBreadCrumbs(breadCrumbData ?? [], ROUTES.REPORTS, breadcrumbSearchParams, true)
  const preparedBreadCrumbList = instructorMode?.instructorName != null
    ? [...baseBreadCrumbList, { title: instructorMode.instructorName, path: '' }]
    : baseBreadCrumbList
  const { mutate: triggerReportsFetch, mutateAsync: fetchReportsMutation, data: reports, error: reportError, isError: isReportsError, isPending: isReportsPending } = useMutation<ReportsV2, any, Record<string, any> | undefined>({
    meta: { silent: true },
    mutationFn: async (payload?: Record<string, any>): Promise<ReportsV2> => {
      const safeData = payload ?? {}
      const requestType = getReportsRequestType(selectedTrend, safeData)

      if (requestType === 'history') {
        setResponseFilterOverrideSurveyItem(null)
        setResponseFilterHighlightedAnswers([])
        setResponseFilterExistingCalculations(null)
        setOriginalBaseReport(null)
        if (instructorMode) {
          const userId = instructorMode.instructorId ?? instructorMode.selfUserId ?? ''
          return await UserManager.getUserReportHistory(
            userId,
            {
              limit: selectedTrend,
              calculations: [...allCalculationKeys],
              ...(instructorMode.objectId != null ? { object_id: instructorMode.objectId } : {})
            },
            params
          )
        }
        return await DivisionManager.getHistoricalTrendsReports({
          params,
          divisionId: divisionId ?? '',
          data: {
            limit: selectedTrend,
            calculations: [...allCalculationKeys]
          }
        })
      }

      if (requestType === 'responders') {
        const respondersRequestData: SurveyResponseFilter = {
          itemId: String(safeData.itemId),
          revision: Number(safeData.revision),
          targetAnswers: Array.isArray(safeData.targetAnswers)
            ? safeData.targetAnswers.map(String)
            : Array.isArray(safeData.answers)
              ? safeData.answers.map(String)
              : [],
          calculations: [...allCalculationKeys],
          ...(safeData.surveySectionId != null ? { surveySectionId: String(safeData.surveySectionId) } : {}),
          ...(safeData.surveySectionRevision != null ? { surveySectionRevision: Number(safeData.surveySectionRevision) } : {}),
          ...(safeData.instructorId !== undefined ? { instructorId: safeData.instructorId } : {})
        }

        const respondersResponse = await DivisionManager.getReportsResponders({
          params,
          divisionId: divisionId ?? '',
          data: respondersRequestData
        })

        const fallbackTitle: string = completionRate?.title ?? 'Current Term'
        setResponseFilterOverrideSurveyItem(normalizeNonMultiSurveyItem(respondersResponse.existingSurveyItem))
        setResponseFilterHighlightedAnswers(respondersRequestData.targetAnswers)
        setResponseFilterExistingCalculations(normalizeNonMultiReportCalculations(respondersResponse.existingReportCalculations))
        setResponseFilterWarningMessage(null)
        return normalizeNonMultiReport(respondersResponse.responseReport, fallbackTitle)
      }

      setResponseFilterOverrideSurveyItem(null)
      setResponseFilterHighlightedAnswers([])
      setResponseFilterExistingCalculations(null)

      const baseTarget = instructorMode
        ? {
            target_type: 'user' as const,
            object_id: instructorMode.instructorId ?? instructorMode.selfUserId ?? '',
            ...(instructorMode.objectId != null ? { scope_object_id: instructorMode.objectId } : {})
          }
        : { target_type: 'object' as const, object_id: divisionId ?? '' }

      // For instructor compare targets, attach the same scope used by the base target
      // so the backend narrows each compared instructor to the same division/section scope.
      const scopeObjectId = instructorMode ? instructorMode.objectId : divisionId
      const compareTargets = (safeData.compareTargets ?? []).map((target: any) =>
        target.target_type === 'user' && scopeObjectId
          ? { ...target, scope_object_id: scopeObjectId }
          : target
      )

      const compareResult = await UserManager.getGenericCompare({
        base_target: baseTarget,
        compare_targets: compareTargets,
        slice_ids: safeData.sliceIds ?? [],
        calculations: [...allCalculationKeys]
      }, params)
      pendingReportAccessRef.current = compareResult.reportAccess
      return compareResult.data
    },
    onSuccess: (data: any, payload) => {
      setReportAccessLevel(pendingReportAccessRef.current)
      const requestType = getReportsRequestType(selectedTrend, payload ?? {})
      const hasBasePayload = requestType === 'compare' && !payload?.compareTargets?.length && !payload?.sliceIds?.length && selectedTrend === 1

      if (!hasBasePayload) {
        return
      }

      // Once the base report has loaded, keep its section shape available for
      // later pending states so comparison loads reuse the exact known card count.
      setBaseReportLoadingSections(buildLoadingSectionsFromBaseReport(data?.surveySections))
      setOriginalBaseReport(data)
    }
  })
  const fetchReports = async (payload?: Record<string, any>): Promise<any> => {
    const resolvedPayload = getFinalReportPayload(payload, displaySettings, scopedSliceKeys)
    return await fetchReportsMutation(resolvedPayload)
  }

  const applyDisplaySettingsSubmission = async (nextSettingsInput: DisplaySettings): Promise<void> => {
    const nextSettings = normalizeDisplaySettings(nextSettingsInput)
    const formattedData = getFinalReportPayload(undefined, nextSettings, scopedSliceKeys)

    if (nextSettings.responseFilter == null) {
      setResponseFilterAnswerSelections([])
      setResponseFilterWarningMessage(null)
    }

    dispatch({ type: ReportsActionType.SET_DISPLAY_SETTINGS, payload: nextSettings })
    dispatch({
      type: ReportsActionType.SET_DISPLAY_SETTINGS_CHANGED,
      payload: !isBaseReportDisplaySettings(nextSettings)
    })

    await fetchReportsMutation(formattedData)
  }

  const hasCompareOrSliceSelection = (settings: DisplaySettings): boolean => (
    settings.comparisons.some((comparison) => comparison.comparison != null) ||
    settings.slices.length > 0
  )

  const handleDisplaySettingsSubmit = async (settings: DisplaySettings): Promise<void> => {
    const nextSettings = normalizeDisplaySettings(settings)
    const shouldConfirmLeaveResponseFilter = displaySettings.responseFilter != null && hasCompareOrSliceSelection(nextSettings)

    if (shouldConfirmLeaveResponseFilter) {
      setPendingDisplaySettingsSubmission({
        ...nextSettings,
        responseFilter: null
      })
      return
    }

    await applyDisplaySettingsSubmission(nextSettings)
  }

  const getSelectedResponseCountTotal = (selections: ResponseFilterAnswerSelection[]): number =>
    selections.reduce((total, selection) => total + selection.count, 0)

  const applySurveyResponseFilter = async ({
    itemId,
    revision,
    prompt,
    questionNumber,
    sectionTitle,
    answer,
    count,
    surveySectionId,
    surveySectionRevision,
    instructorId
  }: SurveyResponseMenuSelection): Promise<void> => {
    const activeResponseFilter = displaySettings.responseFilter
    const nextSelections = activeResponseFilter?.itemId === itemId
      ? [
          ...responseFilterAnswerSelections.filter((selection) => selection.answer !== answer),
          { answer, count }
        ]
      : [{ answer, count }]
    const nextAnswers = nextSelections.map((selection) => selection.answer)
    const selectedResponseCountTotal = getSelectedResponseCountTotal(nextSelections)

    const nextSettings = {
      ...displaySettings,
      comparisons: [{ compareBy: null, comparison: null, comparisonOption: null }],
      slices: [],
      responseFilter: {
        itemId,
        revision,
        prompt,
        answers: nextAnswers,
        sectionTitle,
        questionNumberLabel: `Q${questionNumber}`,
        surveySectionId,
        surveySectionRevision,
        instructorId
      }
    }

    setResponseFilterAnswerSelections(nextSelections)
    setResponseFilterHighlightedAnswers(nextAnswers)
    dispatch({
      type: ReportsActionType.SET_DISPLAY_SETTINGS,
      payload: nextSettings
    })
    dispatch({
      type: ReportsActionType.SET_DISPLAY_SETTINGS_CHANGED,
      payload: true
    })

    if (selectedResponseCountTotal < RESPONSE_FILTER_MINIMUM_COUNT) {
      setResponseFilterWarningMessage(RESPONSE_FILTER_WARNING_MESSAGE)
      return
    }

    setResponseFilterWarningMessage(null)
    pendingResponseFilterScrollRestoreRef.current = window.scrollY
    await fetchReports({
      itemId,
      revision,
      prompt,
      targetAnswers: nextAnswers,
      calculations: [...allCalculationKeys],
      surveySectionId,
      surveySectionRevision,
      instructorId
    })
  }

  const handleFilterBySurveyResponse = async (selection: SurveyResponseMenuSelection): Promise<void> => {
    if (shouldConfirmResponseFilterSwitch(displaySettings, selection.itemId)) {
      setPendingResponseFilterSelection(selection)
      return
    }

    await applySurveyResponseFilter(selection)
  }

  const handleCloseResponseFilterSwitchModal = (): void => {
    setPendingResponseFilterSelection(null)
  }

  const handleConfirmResponseFilterSwitch = async (): Promise<void> => {
    if (!pendingResponseFilterSelection) {
      return
    }

    const nextSelection = pendingResponseFilterSelection
    setPendingResponseFilterSelection(null)
    await applySurveyResponseFilter(nextSelection)
  }

  const handleCloseLeaveResponseFilterModal = (): void => {
    setPendingDisplaySettingsSubmission(null)
  }

  const handleConfirmLeaveResponseFilter = async (): Promise<void> => {
    if (!pendingDisplaySettingsSubmission) {
      return
    }

    const nextSettings = pendingDisplaySettingsSubmission
    setPendingDisplaySettingsSubmission(null)
    await applyDisplaySettingsSubmission(nextSettings)
  }

  const handleRemoveResponseFilterAnswer = async (answerToRemove: string): Promise<void> => {
    const activeResponseFilter = displaySettings.responseFilter

    if (!activeResponseFilter) {
      return
    }

    const nextSelections = responseFilterAnswerSelections.filter((selection) => selection.answer !== answerToRemove)
    const nextAnswers = activeResponseFilter.answers.filter((answer) => answer !== answerToRemove)

    if (nextAnswers.length === 0) {
      setResponseFilterAnswerSelections([])
      setResponseFilterWarningMessage(null)
      dispatch({
        type: ReportsActionType.SET_DISPLAY_SETTINGS,
        payload: {
          ...displaySettings,
          responseFilter: null
        }
      })
      dispatch({
        type: ReportsActionType.SET_DISPLAY_SETTINGS_CHANGED,
        payload: false
      })
      setResponseFilterOverrideSurveyItem(null)
      setResponseFilterHighlightedAnswers([])
      setResponseFilterExistingCalculations(null)

      await fetchReportsMutation({
        calculations: [...allCalculationKeys],
        compareTargets: [],
        sliceIds: []
      })
      return
    }

    dispatch({
      type: ReportsActionType.SET_DISPLAY_SETTINGS,
      payload: {
        ...displaySettings,
        responseFilter: {
          ...activeResponseFilter,
          answers: nextAnswers
        }
      }
    })
    dispatch({
      type: ReportsActionType.SET_DISPLAY_SETTINGS_CHANGED,
      payload: true
    })

    setResponseFilterAnswerSelections(nextSelections)
    setResponseFilterHighlightedAnswers(nextAnswers)

    if (getSelectedResponseCountTotal(nextSelections) < RESPONSE_FILTER_MINIMUM_COUNT) {
      setResponseFilterWarningMessage(RESPONSE_FILTER_WARNING_MESSAGE)
      return
    }

    setResponseFilterWarningMessage(null)
    pendingResponseFilterScrollRestoreRef.current = window.scrollY
    await fetchReports({
      itemId: activeResponseFilter.itemId,
      revision: activeResponseFilter.revision,
      prompt: activeResponseFilter.prompt,
      targetAnswers: nextAnswers,
      calculations: [...allCalculationKeys],
      surveySectionId: activeResponseFilter.surveySectionId,
      surveySectionRevision: activeResponseFilter.surveySectionRevision,
      instructorId: activeResponseFilter.instructorId
    })
  }

  const { data: completionRate = null, isError: isCompletionRateError, error: completionRateError, isLoading: completionRateLoading } = useQuery({
    queryKey: ['completionRate', divisionId, seriesId, termId],
    queryFn: async () => (await DivisionManager.getCompletionRate(divisionId ?? '', params)),
    enabled: !!divisionId && !!seriesId && !!termId && !instructorMode,
    retry: false
  })

  const { data: instructorCompletionRate = null, isError: isInstructorCompletionRateError } = useQuery({
    queryKey: ['instructorCompletionRate', instructorMode?.instructorId, instructorMode?.selfUserId, instructorMode?.objectId, seriesId, termId],
    queryFn: async () => {
      const userId = instructorMode?.instructorId ?? instructorMode?.selfUserId
      return await UserManager.getGenericCompletionRate({
        ...params,
        target_type: 'user',
        object_id: userId,
        ...(instructorMode?.objectId != null ? { scope_object_id: instructorMode.objectId } : {})
      })
    },
    enabled: !!instructorMode && !!seriesId && !!termId && !!(instructorMode?.instructorId ?? instructorMode?.selfUserId),
    retry: false,
    select: (data: any): SurveyCompletionRate => ({
      ...data,
      categoryName: 'Instructor' as unknown as DivisionCategory,
      title: instructorMode?.instructorName ?? data.title,
      surveyScore: data.surveyScore ?? reports?.surveyScores?.[0] ?? null,
      children: [],
      instructorDetails: []
    })
  })

  // There used to be a client-side fallback here that summed `completionDetails` across every
  // card the dashboard had loaded. It is gone, along with the `instructorDistributions` config
  // field that fed it: the dashboard now sends one page of cards, so that sum would silently
  // become "this page only" and present a partial figure as the instructor's overall rate.
  //
  // Safe to drop because the server-side value is reliable — it cannot be null on a 200 (an
  // instructor with no sections gets a well-formed zeroed body), and its only 404 path
  // requires a series with no slot, which cannot happen when the series is derived from a
  // card. A non-200 now surfaces as an error rather than being papered over; see
  // `effectiveIsCompletionRateError` below.
  // Spec: docs/specs/active/2026-08-19-instructor-dashboard-pagination/spec.md (BC-6)
  const effectiveCompletionRate = instructorMode
    ? instructorCompletionRate
    : completionRate

  // `isCompletionRateError` comes from the division query, which is disabled in instructor
  // mode — so before this, a failed instructor completion-rate call could not surface at all.
  const effectiveIsCompletionRateError = instructorMode
    ? isInstructorCompletionRateError
    : isCompletionRateError
  const percent = effectiveCompletionRate?.completionPercent ?? 0

  // Scope chips, the slice-picker lock, and the Report Insights subtitle all read
  // this. It is fetched from `/api/roles/current_user/?base_division_id=…` only
  // for the Viewer role, keyed on the division the report is currently on
  // (`breadcrumbScopeId` = the selected division id, or the instructor's scope
  // division). This changes on every reports-dropdown switch, so the query
  // refetches each time; non-Viewers pass `undefined`, which disables the query
  // so the call is never made.
  const scopeBaseDivisionId = hasRole(ROLES.VIEWER) ? (breadcrumbScopeId ?? undefined) : undefined
  const { appliedAttributeScope } = useFetchCurrentUserAttributeScope(scopeBaseDivisionId)
  const scopedSliceKeys = useMemo(
    () => new Set(appliedAttributeScope.courseAttributes.map(e => scopedSliceKey(e.title, e.value))),
    [appliedAttributeScope]
  )
  const currentReports = reports ?? emptyReports
  // Instructor mode skips LoadingPageV2, and ReportsFeature used to treat a
  // missing completionRate as "No results found". Keep the insights skeleton
  // up until the instructor completion-rate query has a body or a real error.
  // `reports === undefined` still covers the first paint before the mutation
  // starts; the completion-rate clause also covers project/term switches where
  // a previous mutation still holds stale reports.
  // Spec: docs/specs/active/bugfix-instructor-report-empty-flash/spec.md
  const instructorCompletionQueryEnabled = Boolean(
    instructorMode &&
    seriesId &&
    termId &&
    (instructorMode.instructorId ?? instructorMode.selfUserId)
  )
  const effectiveIsReportsPending = isReportsPending ||
    (instructorMode != null && seriesId != null && termId != null && reports === undefined && !isReportsError) ||
    (instructorCompletionQueryEnabled && !effectiveCompletionRate && !effectiveIsCompletionRateError)
  const reportLoadingSections = useMemo(() => {
    // Reports should never depend on template-editor access just to draw a
    // loading skeleton. Reuse the known base report shape when available and
    // otherwise fall back to the generic placeholder cards.
    return resolveReportInsightsLoadingSections(baseReportLoadingSections)
  }, [baseReportLoadingSections])
  const loadingTitles = useMemo(() => {
    if (!effectiveIsReportsPending) {
      return null
    }

    const baseReportTitle = currentReports.titles[0] ?? effectiveCompletionRate?.title
    // Loading badges must be generated from the same report title rules as the
    // resolved view so the chip labels do not change shape after the fetch.
    const responseFilterQuestionOptions = getResponseFilterQuestionOptions(originalBaseReport ?? currentReports)
    return buildReportTitlesFromSettings(baseReportTitle, displaySettings, responseFilterQuestionOptions)
  }, [effectiveCompletionRate?.title, currentReports, displaySettings, effectiveIsReportsPending, originalBaseReport])

  const requestCommentsExport = async (questionScope?: { surveyItemId: string, revisionId: number }): Promise<void> => {
    const allowed = await checkStatusBeforeExport()
    if (!allowed) return

    const payload = buildCommentsExportPayload(displaySettings, questionScope)
    let response: any
    if (instructorMode) {
      response = instructorMode.instructorId
        ? await UserManager.getUserReportCommentsExport(instructorMode.instructorId, payload, params)
        : await UserManager.getSelfUserReportCommentsExport(payload, params)
    } else {
      const exportDivisionId = breadCrumbData?.[breadCrumbData.length - 1]?.id
      if (!exportDivisionId) {
        notifyError(null, { fallbackMessage: 'Export is unavailable because report data failed to load. Please refresh the page.' })
        return
      }
      response = await DivisionManager.getDivisionCommentsCSVExport(exportDivisionId, params, payload)
    }

    if (response.status === 202) {
      // Camelized by the shared api client's axios-case-converter middleware — the Division
      // path (export_comments_csv_report) genuinely returns a real task_id; reading the
      // backend's snake_case name here always read undefined, so tracking silently never
      // started. The User-report path never sends task_id at all, so it still correctly
      // falls through to the message-only branch below.
      if (response.data?.taskId) {
        setTaskId(response.data.taskId)
        setShowBackgroundModal(true)
      } else {
        notification(response.data?.message ?? 'Comments export started.', { variant: 'info', subTitle: isGlobalRole ? 'Check the Activity Monitor for status.' : 'You will be notified when it is complete.' })
      }
      return
    }

    notification(
      response.data && typeof response.data === 'object' && 'message' in response.data
        ? (response.data.message as string)
        : 'Comments export request queued.',
      { variant: 'success' }
    )
  }

  const downloadFile = async (fileType: string, exportConfig?: ExportConfig): Promise<void> => {
    const allowed = await checkStatusBeforeExport()
    if (!allowed) return

    try {
      if (exportConfig?.exportBy === 'Comments Export') {
        if (!instructorMode && selectedTrend !== 1) {
          notification('Comments export is only available for the Current Term report view.', { variant: 'info' })
          return
        }
        await requestCommentsExport()
        return
      }

      if (instructorMode) {
        const exportParams: Record<string, any> = {
          term_id: termId,
          distribution_series_id: seriesId
        }
        if (instructorMode.instructorId && instructorMode.objectId) {
          exportParams.object_id = instructorMode.objectId
        }
        if (exportConfig) {
          Object.assign(exportParams, buildExportConfigParams(exportConfig))
        }

        const exportResponse = fileType === 'pdf'
          ? (instructorMode.instructorId
              ? await UserManager.getUserReportPDFExport(instructorMode.instructorId, exportParams)
              : await UserManager.getSelfUserReportPDFExport(exportParams))
          : (instructorMode.instructorId
              ? await UserManager.getUserReportCSVExport(instructorMode.instructorId, exportParams)
              : await UserManager.getSelfUserReportCSVExport(exportParams))
        const contentDisposition = exportResponse.headers?.['content-disposition'] ?? exportResponse.headers?.contentDisposition
        const serverFileName = contentDisposition?.match(/filename="?(.*?)"?$/)?.[1]
        const fallbackExt = contentDisposition?.includes('zip') ? 'zip' : fileType
        const fileName = serverFileName ?? `instructor-report.${fallbackExt}`
        const responseData = exportResponse.data
        const blob = responseData instanceof Blob ? responseData : new Blob([responseData], { type: exportResponse.headers?.['content-type'] ?? (fileType === 'pdf' ? 'application/pdf' : 'text/csv') })
        saveAs(blob, fileName)
        return
      }

      const exportDivisionId = breadCrumbData?.[breadCrumbData.length - 1]?.id
      if (!exportDivisionId) {
        notifyError(null, { fallbackMessage: 'Export is unavailable because report data failed to load. Please refresh the page.' })
        return
      }

      const fetchFile = fileType === 'csv'
        ? DivisionManager.getDivisionCSVExport
        : DivisionManager.getDivisionPDFExport

      // Build params with optional API query parameters
      const exportParams: any = { ...params }
      if (exportConfig) {
        if (exportConfig.shouldIncludeByInstructor) {
          exportParams.byInstructor = 'true'
        }
        if (exportConfig.shouldIncludeByReportingHierarchy) {
          exportParams.byReportingHierarchy = 'true'
        }
        if (exportConfig.shouldIncludeMergePdf) {
          exportParams.mergePdf = 'true'
        }
        // Set export_by to 'Section' when exporting by Course Section (required for bulk export)
        if (exportConfig.exportBy === 'by Course Section') {
          exportParams.export_by = 'Section'
        }
        // Set export_by to 'responses' when exporting by individual response (required for bulk export)
        if (exportConfig.exportBy === 'Individual Responses') {
          exportParams.export_by = 'responses'
        }
        // Set export_by to 'instructor' when exporting by Instructor (emails a ZIP organized by instructor)
        if (exportConfig.exportBy === 'by Instructor') {
          exportParams.export_by = 'instructor'
          if (exportConfig.mergePDFs) {
            exportParams.mergePdf = 'true'
          }
        }
      }

      const exportResponse = await fetchFile(exportDivisionId, exportParams)

      if (exportResponse.status === 202) {
        // The shared api client's axios-case-converter middleware camelizes every response
        // key, so the backend's task_id always arrives as taskId — reading task_id here
        // silently read undefined and skipped tracking, even though this export_by flow
        // (division_report_service.py's send_email_bulk_survey_responses_task) genuinely
        // returns a real task_id.
        if (exportResponse.data?.taskId) {
          setTaskId(exportResponse.data.taskId)
          setShowBackgroundModal(true)
        } else {
          notification(exportResponse.data?.message ?? 'Your export is running in the background.', { variant: 'info' })
        }
        return
      }

      // Legacy: bulk export returned 200 with JSON message before backend migration
      if (
        (exportConfig?.exportBy === 'by Course Section' || exportConfig?.exportBy === 'Individual Responses' || exportConfig?.exportBy === 'by Instructor') &&
        exportResponse.data &&
        typeof exportResponse.data === 'object' &&
        'message' in exportResponse.data &&
        !(exportResponse.data instanceof Blob)
      ) {
        notification(exportResponse.data.message as string, { variant: 'success' })
        return
      }

      const blob = exportResponse.data instanceof Blob
        ? exportResponse.data
        : new Blob([exportResponse.data], {
          type: fileType === 'csv' ? 'text/csv' : 'application/pdf'
        })

      saveAs(blob, `report-${exportDivisionId}.${fileType}`)
    } catch (err) {
      // Export endpoints may return a plain-string threshold 403, a structured
      // insufficient_responses 403, or (for blob responseType) a Blob wrapping JSON.
      // Parse those before notifyError so users see the API copy, not a generic fallback.
      const exportMessage = await resolveExportErrorMessage(err)
      if (exportMessage != null) {
        notification(exportMessage, { variant: 'error' })
        return
      }
      notifyError(err, { fallbackMessage: `Failed to export ${fileType.toUpperCase()}. Please try again.` })
    }
  }

  useEffect(() => {
    handleReportNotifications(
      reportError,
      completionRateError,
      isReportsError,
      isCompletionRateError,
      isReportsPending,
      completionRateLoading,
      activeRole?.roleName === ROLES.ACCOUNT_OWNER
    )
  }, [
    reportError?.status,
    completionRateError?.status,
    isReportsError,
    isCompletionRateError,
    activeRole?.roleName
  ])

  useEffect(() => {
    if (isCompletionRateError && !isReportsError) {
      notifyError(completionRateError, { fallbackMessage: 'Failed to load completion rate data. Please try again.' })
    }
  }, [isCompletionRateError])

  useEffect(() => {
    if (isTopBarDivisionsError) notifyError(topBarDivisionsError, { fallbackMessage: 'Failed to load divisions. Please try again.' })
  }, [isTopBarDivisionsError])

  useEffect(() => {
    if (isBreadcrumbError) notifyError(breadcrumbError, { fallbackMessage: 'Failed to load breadcrumb data. Please try again.' })
  }, [isBreadcrumbError])

  useEffect(() => {
    const hasRequiredParams = instructorMode
      ? Boolean(seriesId && termId)
      : Boolean(divisionId && seriesId && termId)
    if (hasRequiredParams) {
      triggerReportsFetch({
        calculations: [...allCalculationKeys],
        compareTargets: [],
        sliceIds: []
      })
    }
  }, [divisionId, seriesId, termId, selectedTrend, instructorMode])

  useEffect(() => {
    setBaseReportLoadingSections(null)
    setResponseFilterOverrideSurveyItem(null)
    setResponseFilterHighlightedAnswers([])
    setResponseFilterExistingCalculations(null)
    setOriginalBaseReport(null)
    setResponseFilterAnswerSelections([])
    setResponseFilterWarningMessage(null)
    setReportAccessLevel(null)
    pendingResponseFilterScrollRestoreRef.current = null
  }, [divisionId, seriesId, termId])

  useEffect(() => {
    if (isReportsPending || pendingResponseFilterScrollRestoreRef.current == null) {
      return
    }

    const scrollTop = pendingResponseFilterScrollRestoreRef.current
    window.scrollTo({ top: scrollTop, behavior: 'auto' })
    pendingResponseFilterScrollRestoreRef.current = null
  }, [isReportsPending, reports])

  // Add loader for mutation API
  if (!instructorMode && (breadcrumbLoading || completionRateLoading || isDivisionLoading)) {
    return <LoadingPageV2 title={PAGE_TITLES.REPORTS} />
  }

  const disableOption = effectiveCompletionRate?.categoryName.toString() === 'Section'
  const instructorViewToggle = fromInstructorId
    ? (
      <Box sx={{ display: 'flex', border: `1px solid ${grey[350]}`, borderRadius: '8px' }}>
        <CustomTooltip title="Survey Card View">
          <GenericButton
            icon={<SpaceDashboardOutlinedIcon />}
            onClick={() => { navigate(`${ROUTES.INSTRUCTOR_SURVEYS}/${fromInstructorId}?view=card&termId=${termId ?? ''}&projectId=${seriesId ?? ''}`) }}
            buttonType={ButtonType.Ghost}
            buttonSize={ButtonSize.Large}
            sx={{ borderRight: `1px solid ${grey[350]}`, borderRadius: '8px 0 0 8px', background: `${grey[0]} !important` }}
          />
        </CustomTooltip>
        <CustomTooltip title="Report View">
          <GenericButton
            icon={<BarChartIcon />}
            onClick={() => { navigate(`${ROUTES.INSTRUCTOR_SURVEYS}/${fromInstructorId}?view=report&termId=${termId ?? ''}&projectId=${seriesId ?? ''}`) }}
            buttonType={ButtonType.Ghost}
            buttonSize={ButtonSize.Large}
            sx={{ borderRadius: '0 8px 8px 0', background: `${grey[100]} !important` }}
          />
        </CustomTooltip>
      </Box>
      )
    : null
  // CE-125: no_access 403s every report endpoint the toolbar drives (export, insights/compare,
  // regenerate) — hide the whole control rather than leaving disabled buttons that 403 on click.
  // CE-210: same rationale when the report is fully suppressed below the Results Threshold
  // (insufficient_responses 403, `hide` behavior) — the report itself is a card, so every export
  // would 403 too. (`qualitative_and_instructor_added` returns 200 with no error, so the toolbar
  // correctly stays there.)
  // Saved Comments is an instructor route, so it is offered on the instructor report view and only
  // for the reader's own dashboard — never on the admin reports page, and never while looking at
  // someone else. It used to be passed on the admin page instead, where `hideMoreButton` hid the
  // button that would have shown it, making the item unreachable.
  const isOwnInstructorDashboard =
    instructorMode != null &&
    (instructorMode.instructorId == null || instructorMode.instructorId === instructorMode.selfUserId)
  const savedCommentsHandler = isOwnInstructorDashboard
    ? () => { navigate(ROUTES.SAVED_COMMENTS) }
    : undefined

  const reportsControls = isNoReportAccessError(reportError) || isInsufficientResponsesError(reportError)
    ? null
    : <ReportsControls
      selectedTab={selectedTab}
      disableButtons={!effectiveCompletionRate}
      downloadPDFReport={async (exportConfig?: ExportConfig) => { await downloadFile('pdf', exportConfig) }}
      downloadCSVFile={async (exportConfig?: ExportConfig) => { await downloadFile('csv', exportConfig) }}
      reports={isReportsError ? null : currentReports}
      fetchReports={fetchReports}
      onSubmitDisplaySettings={handleDisplaySettingsSubmit}
      disableOption={disableOption}
      percent={percent}
      isBulkExportModalOpen={isBulkExportModalOpen}
      onOpenExportModal={() => {
        setExportModalPreset('Aggregate Report')
        setIsBulkExportModalOpen(true)
      }}
      onCloseExportModal={() => {
        setIsBulkExportModalOpen(false)
        setExportModalPreset('Aggregate Report')
      }}
      initialExportBy={exportModalPreset}
      lockExportBy={exportModalPreset === 'Comments Export'}
      hideMoreButton={instructorMode == null || instructorMode.instructorId != null}
      hideRegenerate={instructorMode != null}
      isSectionReport={effectiveCompletionRate?.categoryName?.toString() === 'Section'}
      reportMeta={effectiveCompletionRate}
      sectionSlotStatus={sectionSlotStatus}
      extraControls={instructorViewToggle ?? undefined}
      onSavedComments={savedCommentsHandler}
      bulkExportModalConfig={instructorMode != null ? INSTRUCTOR_BULK_EXPORT_MODAL_PROPS : undefined}
      allowResultsCsvExport={reportAccessLevel !== 'view_results_only'}
      reportAccessLevel={reportAccessLevel}
    />

  const controls = instructorMode?.extraControls
    ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{reportsControls}{instructorMode.extraControls}</Box>
    : reportsControls

  return (
    <AppliedAttributeScopeContext.Provider value={appliedAttributeScope}>
      <CustomHeader info={instructorMode?.instructorName ?? preparedBreadCrumbList?.at(-1)?.title ?? PAGE_TITLES.REPORTS} />
      <SkipNavigation />
      <Container
        maxWidth={false}
        disableGutters={true}
      >
        <Stack sx={{ mt: { xs: 0, md: -6 }, gap: 4 }} spacing={0}>
          <PageHeader
            breadcrumb={preparedBreadCrumbList}
            actions={
              <Box sx={{ paddingTop: 1, paddingBottom: 2 }}>
                <ProjectAndTermHeader filterTermId={instructorMode != null ? termId ?? undefined : undefined} />
              </Box>
            }
            attributes={effectiveCompletionRate && (
              instructorMode != null
                ? (
                  <Box display="flex" alignItems="center" flexWrap="wrap" gap={3}>
                    <ReportAttributes completionRate={effectiveCompletionRate} parentSurveyScore={effectiveCompletionRate?.surveyScore} percent={percent}/>
                    <ReportAttributeLine
                      attributes={effectiveCompletionRate.attributes}
                      surfaceKind="user"
                      variant="inline"
                    />
                  </Box>
                  )
                : (
                  <ReportAttributes
                    completionRate={effectiveCompletionRate}
                    parentSurveyScore={effectiveCompletionRate?.surveyScore}
                    percent={percent}
                    attributesNode={
                      <ReportAttributeLine
                        attributes={effectiveCompletionRate.attributes}
                        surfaceKind="section"
                        variant="inline"
                      />
                    }
                  />
                  )
            )}
            title={effectiveCompletionRate?.title}
            controls={controls}
            statusChip={<ReportHeaderChips isAnonymousSurvey={effectiveCompletionRate?.isAnonymousSurvey ?? false} isSandbox={effectiveCompletionRate?.isSandbox} />}
          />
          <ReportsFeature
            reports={currentReports}
            originalReports={originalBaseReport}
            completionRate={effectiveCompletionRate}
            reportError={reportError ?? null}
            activeRoleName={activeRole?.roleName}
            parentSurveyScore={effectiveCompletionRate?.surveyScore}
            isReportsPending={effectiveIsReportsPending}
            isCompletionRateError={effectiveIsCompletionRateError}
            seriesId={seriesId}
            termId={termId}
            pendingTitles={loadingTitles}
            reportLoadingSections={reportLoadingSections}
            setSelectedTab={setSelectedTab}
            onFilterBySurveyResponse={instructorMode != null ? undefined : handleFilterBySurveyResponse}
            responseFilterOverrideSurveyItem={responseFilterOverrideSurveyItem}
            responseFilterHighlightedAnswers={responseFilterHighlightedAnswers}
            responseFilterExistingCalculations={responseFilterExistingCalculations}
            isResponseFilterApplied={Boolean(responseFilterOverrideSurveyItem)}
            responseFilterWarningMessage={responseFilterWarningMessage}
            onRemoveResponseFilterAnswer={handleRemoveResponseFilterAnswer}
            onOpenCommentsExport={() => {
              setExportModalPreset('Comments Export')
              setIsBulkExportModalOpen(true)
            }}
            onRequestCommentsExport={({ surveyItemId, revisionId }) => {
              void requestCommentsExport({ surveyItemId, revisionId })
            }}
            disableFlaggedCommentActions={instructorMode != null}
            fetchSectionTableData={instructorMode != null
              ? async (tableParams: any) => await UserManager.getGenericCompletionRateChildren('Section', {
                target_type: 'user',
                object_id: instructorMode.instructorId ?? instructorMode.selfUserId,
                ...(instructorMode.objectId != null ? { scope_object_id: instructorMode.objectId } : {}),
                termId,
                distributionSeriesId: seriesId,
                ...tableParams
              })
              : undefined
            }
            objectCategoryName={
              instructorMode?.objectId != null && Array.isArray(divisionAndSections)
                ? divisionAndSections.find((d: any) => d.divisionId === instructorMode.objectId)?.categoryName
                : undefined
            }
          />
        </Stack>
      </Container>
      <ExportBackgroundTaskRunningModal
        isOpen={showBackgroundModal}
        onClose={() => { setShowBackgroundModal(false) }}
        taskId={taskId ?? ''}
      />
      <GenericModal
        isOpen={Boolean(pendingResponseFilterSelection)}
        onClose={handleCloseResponseFilterSwitchModal}
        onConfirm={handleConfirmResponseFilterSwitch}
        title="Switch question filter"
      >
        You have active filters on another question. Switching here will clear filters and apply the new selection. Do you want to continue?
      </GenericModal>
      <GenericModal
        isOpen={Boolean(pendingDisplaySettingsSubmission)}
        onClose={handleCloseLeaveResponseFilterModal}
        onConfirm={handleConfirmLeaveResponseFilter}
        title="Leave filter by response"
      >
        You have an active response filter. Continuing will clear it and apply the selected comparison or slice settings. Do you want to continue?
      </GenericModal>
    </AppliedAttributeScopeContext.Provider>
  )
}

const ReportsPageWithProvider: React.FC<ReportsPageProps> = (props) => (
  <ReportsProvider>
    <ReportsPage {...props} />
  </ReportsProvider>
)

export { ReportsPageWithProvider }
export default ReportsPageWithProvider
